import { IconCheck } from '@consta/icons/IconCheck'
import { IconClose } from '@consta/icons/IconClose'
import { IconEdit } from '@consta/icons/IconEdit'
import type { Props } from '@consta/uikit/Button'
import { Button } from '@consta/uikit/Button'
import { FieldGroup } from '@consta/uikit/FieldGroup'
import { Text } from '@consta/uikit/Text'
import { TextField } from '@consta/uikit/TextField'
import { textProps } from '@core/core/helpers'
import type { AlgorithmTypes, Calculation, FormDataML } from '@core/core/types'
import type { FC } from 'react'
import { useEffect, useState } from 'react'

import { FIRST_CALCULATION_ID } from '../../../constants'
import { FaultObjects, HorizonObjects, SeismophationsObjects, StickObjects } from '../Objects'
import css from './CalculationCard.css'

type CalculationCardProps = {
  formData: FormDataML
  calculation: Calculation
  type: AlgorithmTypes
  updateCalculation: (calc: Calculation) => void
  deleteCalculation: () => void
  customClass?: string
}

const buttonProps: Partial<Props> = { onlyIcon: true, size: 'xs', view: 'clear' }

export const CalculationCard: FC<CalculationCardProps> = ({
  formData,
  calculation,
  type,
  customClass = '',
  deleteCalculation,
  updateCalculation,
}) => {
  const [showRenameField, setShowRenameField] = useState<boolean>(false)
  const [newName, setNewName] = useState<string>('')
  const [cubeName, setCubeName] = useState<string>()

  useEffect(() => {
    const cubeName = formData.calculations.find(
      calculation => calculation.id === FIRST_CALCULATION_ID,
    )?.params?.seismicSurvey.entity?.entityName
    setCubeName(cubeName)
  }, [formData.calculations])

  const toggleRenameField = () => {
    setShowRenameField(!showRenameField)
  }
  const renameCalculation = () => {
    updateCalculation({ ...calculation, name: newName })
    setShowRenameField(false)
    setNewName('')
  }

  function getObjects(type: AlgorithmTypes) {
    const props = { calculation, updateCalculation }
    switch (type) {
      case 'horizonsExtension':
        return <HorizonObjects {...props} cubeName={cubeName} />
      case 'faultsSeparation':
        return <FaultObjects {...props} />
      case 'seismophations':
        return <SeismophationsObjects {...props} formData={formData} />
      case 'stickSeparation':
        return <StickObjects {...props} />
    }
  }

  const Objects = getObjects(type)

  return (
    <div className={`${css.container} ${customClass}`}>
      <div className={css.header}>
        <Text truncate={true} {...textProps}>
          {calculation.name}
        </Text>
        <div>
          <Button
            view={showRenameField ? 'ghost' : 'clear'}
            iconLeft={IconEdit}
            onClick={toggleRenameField}
            {...buttonProps}
          />
          <Button
            iconLeft={IconClose}
            disabled={calculation.id === FIRST_CALCULATION_ID}
            onClick={deleteCalculation}
            {...buttonProps}
          />
        </div>
      </div>
      {!!showRenameField && (
        <FieldGroup className={css.rename} size="xs">
          <TextField
            placeholder={calculation.name}
            value={newName}
            onChange={value => {
              setNewName(value ?? '')
            }}
          />
          <Button
            view="secondary"
            onlyIcon={true}
            iconLeft={IconCheck}
            onClick={renameCalculation}
          />
        </FieldGroup>
      )}
      <div className={css.body}>{Objects}</div>
    </div>
  )
}
